//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { IPromise, IQService } from 'angular';

// Fetch-based API function alias
type ApiFn<T extends any[], U> = (...args: T) => Promise<U>;
// AngularJs API function alias
type NgApiFn<T extends any[], U> = (...args: T) => IPromise<U>;

// `any` shorthands
type AnyApiFn = ApiFn<any[], any>;
type AnyNgApiFn = NgApiFn<any[], any>;

// Base type representing any ES6 API module
interface IApiModule {
  [key: string]: AnyApiFn;
}

// Computes the type of the produced AngularJs service adapter for an API module
type NgApiAdapter<T extends IApiModule> = {
  [key in keyof T]: T[key] extends ApiFn<infer TArgs, infer TRet> ? NgApiFn<TArgs, TRet> : never;
};

/**
 * Creates an adapter that allows an ES6-API-Module to be used as an Angularjs Service.
 * @param $q Q service instance
 * @param apiModule The api module to adapt
 */
function createNgApiAdapter<T extends IApiModule>($q: IQService, apiModule: T): NgApiAdapter<T> {
  return Object.entries(apiModule).
    map(([endpointName, apiFn]: [string, AnyApiFn]) => {
      return [endpointName, (...args: any[]) => $q.when(apiFn(...args))] as const;
    }).
    reduce<Record<string, AnyNgApiFn>>(
      (acc, next) => ({ ...acc, [next[0]]: next[1] }),
      {},
    ) as NgApiAdapter<T>;
}

/**
 * Adapts an ES6 API module into an Angularjs Service.
 * @param apiModule The api module to adapt
 */
export function createNgApiAdapterService<T extends IApiModule>(apiModule: T)
: ['$q', ($q: IQService) => NgApiAdapter<T>] {
  return ['$q', ($q: IQService) => createNgApiAdapter($q, apiModule)];
}

/**
 * Gets an adapted API Service's type signature
 */
export type GetNgApiAdapterType<T extends ['$q', ($q: IQService) => NgApiAdapter<any>]> = ReturnType<T[1]>;
